import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  FiEdit2,
  FiHome,
  FiPlus,
  FiShield,
  FiTrash2,
  FiUser,
  FiUsers,
} from 'react-icons/fi';

import BasePage from '../../../components/BasePage';
import DataTable, { Column, FilterField } from '../../../components/DataTable';

import api from '../../../services/api';
import { useAuth } from '../../../hooks/auth';
import { useToast } from '../../../hooks/useToast';

import PasswordField from './PasswordField';
import {
  Field,
  FieldGroup,
  GhostButton,
  ModalBody,
  MutedCell,
  PageContainer,
  PageHeader,
  PrimaryButton,
  RoleChip,
  RowAction,
  SectionLabel,
  StatCard,
  StatsRow,
  SyndicateChip,
  UserCell,
} from './styles';

interface IUserInfo {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  syndicate_id?: string;
  syndicate?: { id?: string; nome_fantasia?: string };
}

interface IUnion {
  id: string;
  nome_fantasia: string;
}

interface IFormValues {
  id?: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  syndicate: string;
  password: string;
  password_confirmation: string;
}

const ROLE_LABEL: Record<string, string> = {
  admin: 'Administrador',
  syndicate: 'Sindicato',
};

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Administrador' },
  { value: 'syndicate', label: 'Sindicato' },
];

const initials = (first: string, last: string): string => {
  const f = first?.[0] || '';
  const l = last?.[0] || '';
  return `${f}${l}`.toUpperCase() || '?';
};

const addValidationSchema = Yup.object().shape({
  first_name: Yup.string().required('O nome é obrigatório.'),
  last_name: Yup.string().required('O sobrenome é obrigatório.'),
  email: Yup.string()
    .email('E-mail inválido')
    .required('O e-mail é obrigatório.'),
  syndicate: Yup.string(),
  role: Yup.string(),
  password: Yup.string()
    .min(6, 'Mínimo de 6 caracteres')
    .required('A senha é obrigatória.'),
  password_confirmation: Yup.string()
    .min(6, 'Mínimo de 6 caracteres')
    .oneOf([Yup.ref('password'), null], 'As senhas devem ser idênticas.')
    .required('Confirme a senha.'),
});

const editValidationSchema = Yup.object().shape({
  first_name: Yup.string().required('O nome é obrigatório.'),
  last_name: Yup.string().required('O sobrenome é obrigatório.'),
  email: Yup.string()
    .email('E-mail inválido')
    .required('O e-mail é obrigatório.'),
  syndicate: Yup.string(),
  role: Yup.string(),
  password: Yup.string().test(
    'min-if-filled',
    'Mínimo de 6 caracteres',
    v => !v || v.length >= 6,
  ),
  password_confirmation: Yup.string().oneOf(
    [Yup.ref('password'), ''],
    'As senhas devem ser idênticas.',
  ),
});

const Users: React.FC = () => {
  const { user } = useAuth();
  const { addToast } = useToast();

  const isAdmin = user.role === 'admin';

  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<IUserInfo[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterParams, setFilterParams] = useState<Record<string, string>>({});

  const [unions, setUnions] = useState<IUnion[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingUser, setPendingUser] = useState<IUserInfo | null>(null);

  useEffect(() => {
    document.title = 'Usuários - Viva Voz';
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<IUserInfo[]>('/users', {
        params: { ...filterParams, page: currentPage },
      });
      setUsers(response.data);
      setTotalUsers(Number(response.headers['x-total-users'] || 0));
    } finally {
      setLoading(false);
    }
  }, [filterParams, currentPage]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (!isAdmin) return;
    api
      .get<IUnion[]>('/unions')
      .then(res => setUnions(res.data))
      .catch(() => undefined);
  }, [isAdmin]);

  /* ─── Actions ─── */

  const handleOpenEdit = useCallback(
    async (id: string) => {
      try {
        const response = await api.get<IUserInfo>(`/users/${id}`);
        setPendingUser(response.data);
        setShowEditModal(true);
      } catch {
        addToast({
          title: 'Erro',
          body: 'Não foi possível carregar o usuário.',
        });
      }
    },
    [addToast],
  );

  const handleOpenDelete = useCallback((userInfo: IUserInfo) => {
    setPendingUser(userInfo);
    setShowDeleteModal(true);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!pendingUser) return;
    try {
      await api.delete(`/users/${pendingUser.id}`);
      addToast({ title: 'Usuário removido', body: 'O usuário foi excluído.' });
      setShowDeleteModal(false);
      setPendingUser(null);
      loadUsers();
    } catch {
      addToast({ title: 'Erro', body: 'Falha ao remover o usuário.' });
    }
  }, [pendingUser, addToast, loadUsers]);

  const handleCreate = useCallback(
    async (values: IFormValues, reset: () => void) => {
      setSubmitting(true);
      try {
        const payload = {
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
          password: values.password,
          role: isAdmin ? values.role || 'syndicate' : user.role,
          syndicate_id: isAdmin ? values.syndicate || null : user.syndicate_id,
        };

        await api.post('/users/create', payload);
        addToast({
          title: 'Usuário criado',
          body: 'O cadastro foi salvo com sucesso.',
        });
        reset();
        setShowAddModal(false);
        loadUsers();
      } catch {
        addToast({ title: 'Erro', body: 'Não foi possível criar o usuário.' });
      } finally {
        setSubmitting(false);
      }
    },
    [addToast, isAdmin, loadUsers, user.role, user.syndicate_id],
  );

  const handleUpdate = useCallback(
    async (values: IFormValues) => {
      if (!values.id) return;
      setSubmitting(true);
      try {
        const payload: Record<string, unknown> = {
          id: values.id,
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
        };

        if (isAdmin) {
          payload.role = values.role;
          payload.syndicate_id = values.syndicate || null;
        }

        if (values.password) {
          payload.password = values.password;
          payload.password_confirmation = values.password_confirmation;
        }

        await api.put(`/users/${values.id}`, payload);
        addToast({
          title: 'Usuário atualizado',
          body: 'As alterações foram salvas.',
        });
        setShowEditModal(false);
        setPendingUser(null);
        loadUsers();
      } catch {
        addToast({ title: 'Erro', body: 'Falha ao atualizar o usuário.' });
      } finally {
        setSubmitting(false);
      }
    },
    [addToast, isAdmin, loadUsers],
  );

  /* ─── Table config ─── */

  const filters = useMemo<FilterField[]>(() => {
    const base: FilterField[] = [
      {
        key: 'name',
        label: 'Nome',
        type: 'text',
        placeholder: 'Buscar por nome',
        span: 3,
      },
      {
        key: 'email',
        label: 'E-mail',
        type: 'text',
        placeholder: 'Buscar por e-mail',
        span: 3,
      },
    ];
    if (isAdmin) {
      base.push({
        key: 'role',
        label: 'Função',
        type: 'select',
        placeholder: 'Todas',
        options: ROLE_OPTIONS,
        span: 2,
      });
      base.push({
        key: 'syndicate_id',
        label: 'Sindicato',
        type: 'select',
        placeholder: 'Todos',
        options: unions.map(u => ({ value: u.id, label: u.nome_fantasia })),
        span: 2,
      });
    }
    return base;
  }, [isAdmin, unions]);

  const columns = useMemo<Column<IUserInfo>[]>(() => {
    const base: Column<IUserInfo>[] = [
      {
        key: 'user',
        label: 'Usuário',
        render: u => (
          <UserCell>
            <span className="avatar">
              {initials(u.first_name, u.last_name)}
            </span>
            <div className="info">
              <span className="name" title={`${u.first_name} ${u.last_name}`}>
                {u.first_name}
                {' '}
                {u.last_name}
              </span>
              <span className="email" title={u.email}>
                {u.email}
              </span>
            </div>
          </UserCell>
        ),
      },
      {
        key: 'role',
        label: 'Função',
        width: '14%',
        render: u => (
          <RoleChip
            $role={(() => {
              if (u.role === 'admin') return 'admin';
              if (u.role === 'syndicate') return 'syndicate';
              return 'default';
            })()}
          >
            {u.role === 'admin' ? <FiShield size={11} /> : <FiUser size={11} />}
            {ROLE_LABEL[u.role] || u.role}
          </RoleChip>
        ),
      },
    ];

    if (isAdmin) {
      base.push({
        key: 'syndicate',
        label: 'Sindicato',
        width: '24%',
        render: u =>
          u.syndicate?.nome_fantasia ? (
            <SyndicateChip title={u.syndicate.nome_fantasia}>
              <FiHome size={12} />
              <span>{u.syndicate.nome_fantasia}</span>
            </SyndicateChip>
          ) : (
            <MutedCell>—</MutedCell>
          ),
      });
    }

    return base;
  }, [isAdmin]);

  const handleFiltersChange = useCallback((values: Record<string, string>) => {
    setFilterParams(values);
    setCurrentPage(1);
  }, []);

  /* ─── Derived stats ─── */

  const adminCount = users.filter(u => u.role === 'admin').length;
  const syndicateCount = users.filter(u => u.role === 'syndicate').length;

  return (
    <BasePage title="Usuários">
      <PageContainer>
        <PageHeader>
          <h1>Usuários</h1>
          <span className="subtitle">
            Gerencie os acessos, funções e vínculos sindicais dos usuários da
            plataforma.
          </span>
        </PageHeader>

        <StatsRow>
          <StatCard $tone="brand">
            <span className="icon">
              <FiUsers size={20} />
            </span>
            <div className="body">
              <span className="label">Usuários totais</span>
              <span className="value">{totalUsers}</span>
              <span className="helper">cadastrados no sistema</span>
            </div>
          </StatCard>
          <StatCard $tone="info">
            <span className="icon">
              <FiShield size={20} />
            </span>
            <div className="body">
              <span className="label">Administradores</span>
              <span className="value">{adminCount}</span>
              <span className="helper">na página atual</span>
            </div>
          </StatCard>
          <StatCard $tone="neutral">
            <span className="icon">
              <FiHome size={20} />
            </span>
            <div className="body">
              <span className="label">Sindicato</span>
              <span className="value">{syndicateCount}</span>
              <span className="helper">na página atual</span>
            </div>
          </StatCard>
        </StatsRow>

        <DataTable<IUserInfo>
          title="Lista de usuários"
          subtitle="Filtre por nome, e-mail ou função e gerencie o acesso de cada usuário."
          columns={columns}
          data={users}
          rowKey="id"
          loading={loading}
          page={currentPage}
          pageSize={25}
          total={totalUsers}
          onPageChange={setCurrentPage}
          filters={filters}
          filterValues={filterParams}
          onFiltersChange={handleFiltersChange}
          headerActions={(
            <PrimaryButton type="button" onClick={() => setShowAddModal(true)}>
              <FiPlus size={14} />
              Novo usuário
            </PrimaryButton>
          )}
          rowActions={row => (
            <>
              <RowAction type="button" onClick={() => handleOpenEdit(row.id)}>
                <FiEdit2 size={12} />
                Editar
              </RowAction>
              <RowAction
                type="button"
                $danger
                onClick={() => handleOpenDelete(row)}
                aria-label="Remover usuário"
              >
                <FiTrash2 size={12} />
              </RowAction>
            </>
          )}
          emptyMessage="Nenhum usuário encontrado."
        />
      </PageContainer>

      {/* ─── Delete modal ─── */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
            Remover usuário
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 14,
              color: '#475569',
              margin: 0,
              lineHeight: 1.5,
            }}
          >
            Tem certeza que deseja excluir
            {' '}
            <strong>
              {pendingUser?.first_name}
              {' '}
              {pendingUser?.last_name}
            </strong>
            ? Essa ação não poderá ser desfeita e todos os dados deste usuário
            serão perdidos.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <GhostButton type="button" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </GhostButton>
          <RowAction $danger type="button" onClick={handleDelete}>
            <FiTrash2 size={12} />
            Excluir
          </RowAction>
        </Modal.Footer>
      </Modal>

      {/* ─── Add modal ─── */}
      <Modal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        centered
        size="lg"
      >
        <Formik<IFormValues>
          initialValues={{
            first_name: '',
            last_name: '',
            email: '',
            syndicate: '',
            role: '',
            password: '',
            password_confirmation: '',
          }}
          validationSchema={addValidationSchema}
          onSubmit={(values, helpers) => handleCreate(values, () => helpers.resetForm())}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
          }) => (
            <>
              <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
                  Novo usuário
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <ModalBody as="form" onSubmit={handleSubmit}>
                  <SectionLabel>
                    <FiUser size={11} />
                    Dados pessoais
                  </SectionLabel>

                  <FieldGroup>
                    <Field>
                      <label htmlFor="first_name">
                        Nome *
                        <input
                          id="first_name"
                          name="first_name"
                          placeholder="Primeiro nome"
                          value={values.first_name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </label>
                      {touched.first_name && errors.first_name && (
                        <span className="error">{errors.first_name}</span>
                      )}
                    </Field>
                    <Field>
                      <label htmlFor="last_name">
                        Sobrenome *
                        <input
                          id="last_name"
                          name="last_name"
                          placeholder="Sobrenome"
                          value={values.last_name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </label>
                      {touched.last_name && errors.last_name && (
                        <span className="error">{errors.last_name}</span>
                      )}
                    </Field>
                  </FieldGroup>

                  <FieldGroup $cols={1}>
                    <Field>
                      <label htmlFor="email">
                        E-mail *
                        <input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="email@exemplo.com"
                          value={values.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </label>
                      {touched.email && errors.email && (
                        <span className="error">{errors.email}</span>
                      )}
                    </Field>
                  </FieldGroup>

                  {isAdmin && (
                    <>
                      <SectionLabel>
                        <FiShield size={11} />
                        Permissões
                      </SectionLabel>

                      <FieldGroup>
                        <Field>
                          <label htmlFor="role">
                            Função
                            <select
                              id="role"
                              name="role"
                              value={values.role}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            >
                              <option value="">Selecione uma função</option>
                              {ROLE_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </label>
                        </Field>
                        <Field>
                          <label htmlFor="syndicate">
                            Sindicato
                            <select
                              id="syndicate"
                              name="syndicate"
                              value={values.syndicate}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            >
                              <option value="">Nenhum</option>
                              {unions.map(syn => (
                                <option key={syn.id} value={syn.id}>
                                  {syn.nome_fantasia}
                                </option>
                              ))}
                            </select>
                          </label>
                        </Field>
                      </FieldGroup>
                    </>
                  )}

                  <SectionLabel>
                    <FiShield size={11} />
                    Senha de acesso
                  </SectionLabel>

                  <FieldGroup>
                    <PasswordField
                      id="password"
                      label="Senha *"
                      placeholder="Digite ou gere uma senha"
                      value={values.password}
                      onChange={v => {
                        setFieldValue('password', v);
                        setFieldValue('password_confirmation', v);
                      }}
                      onBlur={handleBlur}
                      error={
                        touched.password && errors.password
                          ? errors.password
                          : undefined
                      }
                    />
                    <PasswordField
                      id="password_confirmation"
                      label="Confirme a senha *"
                      placeholder="Repita a senha"
                      value={values.password_confirmation}
                      onChange={v => setFieldValue('password_confirmation', v)}
                      onBlur={handleBlur}
                      error={
                        touched.password_confirmation &&
                        errors.password_confirmation
                          ? errors.password_confirmation
                          : undefined
                      }
                      showStrength={false}
                      allowGenerate={false}
                    />
                  </FieldGroup>
                </ModalBody>
              </Modal.Body>
              <Modal.Footer>
                <GhostButton
                  type="button"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancelar
                </GhostButton>
                <PrimaryButton
                  type="button"
                  onClick={() => handleSubmit()}
                  disabled={submitting}
                >
                  <FiPlus size={14} />
                  {submitting ? 'Salvando...' : 'Criar usuário'}
                </PrimaryButton>
              </Modal.Footer>
            </>
          )}
        </Formik>
      </Modal>

      {/* ─── Edit modal ─── */}
      <Modal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setPendingUser(null);
        }}
        centered
        size="lg"
      >
        <Formik<IFormValues>
          enableReinitialize
          initialValues={{
            id: pendingUser?.id,
            first_name: pendingUser?.first_name || '',
            last_name: pendingUser?.last_name || '',
            email: pendingUser?.email || '',
            syndicate: pendingUser?.syndicate_id || '',
            role: pendingUser?.role || '',
            password: '',
            password_confirmation: '',
          }}
          validationSchema={editValidationSchema}
          onSubmit={values => handleUpdate(values)}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
          }) => (
            <>
              <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
                  Editar usuário
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <ModalBody as="form" onSubmit={handleSubmit}>
                  <SectionLabel>
                    <FiUser size={11} />
                    Dados pessoais
                  </SectionLabel>

                  <FieldGroup>
                    <Field>
                      <label htmlFor="edit_first_name">
                        Nome *
                        <input
                          id="edit_first_name"
                          name="first_name"
                          placeholder="Primeiro nome"
                          value={values.first_name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </label>
                      {touched.first_name && errors.first_name && (
                        <span className="error">{errors.first_name}</span>
                      )}
                    </Field>
                    <Field>
                      <label htmlFor="edit_last_name">
                        Sobrenome *
                        <input
                          id="edit_last_name"
                          name="last_name"
                          placeholder="Sobrenome"
                          value={values.last_name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </label>
                      {touched.last_name && errors.last_name && (
                        <span className="error">{errors.last_name}</span>
                      )}
                    </Field>
                  </FieldGroup>

                  <FieldGroup $cols={1}>
                    <Field>
                      <label htmlFor="edit_email">
                        E-mail *
                        <input
                          id="edit_email"
                          name="email"
                          type="email"
                          placeholder="email@exemplo.com"
                          value={values.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </label>
                      {touched.email && errors.email && (
                        <span className="error">{errors.email}</span>
                      )}
                    </Field>
                  </FieldGroup>

                  {isAdmin && (
                    <>
                      <SectionLabel>
                        <FiShield size={11} />
                        Permissões
                      </SectionLabel>

                      <FieldGroup>
                        <Field>
                          <label htmlFor="edit_role">
                            Função
                            <select
                              id="edit_role"
                              name="role"
                              value={values.role}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            >
                              <option value="">Selecione uma função</option>
                              {ROLE_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </label>
                        </Field>
                        <Field>
                          <label htmlFor="edit_syndicate">
                            Sindicato
                            <select
                              id="edit_syndicate"
                              name="syndicate"
                              value={values.syndicate}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            >
                              <option value="">Nenhum</option>
                              {unions.map(syn => (
                                <option key={syn.id} value={syn.id}>
                                  {syn.nome_fantasia}
                                </option>
                              ))}
                            </select>
                          </label>
                        </Field>
                      </FieldGroup>
                    </>
                  )}

                  <SectionLabel>
                    <FiShield size={11} />
                    Alterar senha (opcional)
                  </SectionLabel>

                  <FieldGroup>
                    <PasswordField
                      id="edit_password"
                      label="Nova senha"
                      placeholder="Deixe em branco para manter"
                      value={values.password}
                      onChange={v => {
                        setFieldValue('password', v);
                        setFieldValue('password_confirmation', v);
                      }}
                      onBlur={handleBlur}
                      error={
                        touched.password && errors.password
                          ? errors.password
                          : undefined
                      }
                    />
                    <PasswordField
                      id="edit_password_confirmation"
                      label="Confirme a nova senha"
                      placeholder="Repita a senha"
                      value={values.password_confirmation}
                      onChange={v => setFieldValue('password_confirmation', v)}
                      onBlur={handleBlur}
                      error={
                        touched.password_confirmation &&
                        errors.password_confirmation
                          ? errors.password_confirmation
                          : undefined
                      }
                      showStrength={false}
                      allowGenerate={false}
                    />
                  </FieldGroup>
                </ModalBody>
              </Modal.Body>
              <Modal.Footer>
                <GhostButton
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setPendingUser(null);
                  }}
                >
                  Cancelar
                </GhostButton>
                <PrimaryButton
                  type="button"
                  onClick={() => handleSubmit()}
                  disabled={submitting}
                >
                  {submitting ? 'Salvando...' : 'Salvar alterações'}
                </PrimaryButton>
              </Modal.Footer>
            </>
          )}
        </Formik>
      </Modal>
    </BasePage>
  );
};

export default Users;
